import { gridStringOrNumberComparator } from '@mui/x-data-grid-pro';
import { DateFormatPreset, formatDate } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AstroUserAvailabilityPerWeek, AstroUserAvailabilityUser } from '@/generated/gql/graphql';
import { NBSP } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import {
  DataGridConfig,
  GridSortDirectionType,
  UseDataGridConfig,
  gridColDefFactory,
  cellFactory,
  UserCell,
} from '~/components/DataGridCustom';
import { formatDateRange } from '~/util/date/format';

import { HeaderCell } from './cells/HeaderCell/HeaderCell';
import { LinearProgressCell } from './cells/LinearProgressCell/LinearProgressCell';

export type AvailabilityDataGridOptions = {
  columns: { weekStart: Date; weekEnd: Date }[];
};

export type AvailabilityDataGridRow = {
  id: string;
  user: AstroUserAvailabilityUser;
  availabilityPerWeek: AstroUserAvailabilityPerWeek[];
};

export const USER_COLUMN_FIELD = 'user';

export const useAvailabilityDataGridConfig: UseDataGridConfig<AvailabilityDataGridRow, AvailabilityDataGridOptions> = ({
  columns,
}) => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'UserAvailabilityWidget.column' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const headerMap: Record<string, boolean> = {};
    return {
      sortModel: [{ field: USER_COLUMN_FIELD, sort: GridSortDirectionType.ASC }],
      columnVisibilityModel: {},
      columnHeaderHeight: 70,
      columns: [
        createColumn({
          field: USER_COLUMN_FIELD,
          renderCell: cellFactory(UserCell, ({ value }) => ({
            i18ns: I18nNamespace.widgets,
            i18nKeyPrefix: 'UserAvailabilityWidget.cell.user',
            // summary row has an empty id -> hide the avatar
            hideAvatar: !value.id,
          })),
          valueFormatter: (value: { firstName: string; lastName: string }) => `${value.firstName} ${value.lastName}`,
          sortComparator: (v1, v2, cellParams1, cellParams2) => {
            return gridStringOrNumberComparator(v1.lastName, v2.lastName, cellParams1, cellParams2);
          },
          minWidth: 300,
        }),
        ...columns.map((col, i) => {
          const weekStart = new Date(col.weekStart);
          const weekEnd = new Date(col.weekEnd);
          const month = formatDate(DateFormatPreset.MONTH_YEAR, new Date(col.weekStart));
          const headerTitle = headerMap[month] ? NBSP : month;
          headerMap[month] = true;

          const headerName = formatDateRange([weekStart, weekEnd]);

          return createColumn({
            field: `week${i}`,
            headerName,
            renderCell: cellFactory(LinearProgressCell),
            valueGetter: (_value, row) => row.availabilityPerWeek[i].availability,
            minWidth: 300,
            sortable: false,
            resizable: false,
            description: undefined,
            renderHeader: () => <HeaderCell title={headerTitle} subTitle={headerName} />,
          });
        }),
      ],
      exportModel: columns.reduce(
        (acc, _col, i) => {
          acc[`week${i}`] = { numFmt: '0.00%' };
          return acc;
        },
        {} as NonNullable<DataGridConfig<AvailabilityDataGridRow>['exportModel']>,
      ),
    } as DataGridConfig<AvailabilityDataGridRow>;
  }, [t, tColumn, columns]);
};
